/**
 * Mathematical utility functions for option calculations
 */

/**
 * Calculates the cumulative standard normal distribution
 * Uses Abramowitz and Stegun approximation for high accuracy
 */
export function cumulativeNormalDistribution(x: number): number {
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;

  // Save the sign of x
  const sign = x >= 0 ? 1 : -1;
  x = Math.abs(x) / Math.sqrt(2.0);

  // A&S formula 7.1.26
  const t = 1.0 / (1.0 + p * x);
  const y =
    1.0 - ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

  return 0.5 * (1.0 + sign * y);
}

/**
 * Calculates the standard normal probability density function
 */
export function normalProbabilityDensity(x: number): number {
  return Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI);
}

/**
 * Calculates d1 parameter for Black-Scholes formula
 */
export function calculateD1(
  S: number,
  K: number,
  T: number,
  r: number,
  sigma: number,
): number {
  return (
    (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * Math.sqrt(T))
  );
}

/**
 * Calculates d2 parameter for Black-Scholes formula
 */
export function calculateD2(d1: number, sigma: number, T: number): number {
  return d1 - sigma * Math.sqrt(T);
}

/**
 * Converts time to expiry from days to years
 */
export function daysToYears(days: number): number {
  return days / 365;
}

/**
 * Converts implied volatility from percentage to decimal
 */
export function percentageToDecimal(percentage: number): number {
  return percentage / 100;
}
